import type { ExecutionCandlesFetchParams } from "../services/tradeStatisticsApi";
import type { QuoteVariantCandidate } from "../types/instrumentSetup";
import type {
  StrategyRdAppliedLevels,
  StrategyRdApplyOverview,
  StrategyRdCandleType,
  StrategyRdChartParams,
  StrategyRdExcursion,
  StrategyRdExcursionUnit,
  StrategyRdForwardResult,
  StrategyRdLastApply,
  StrategyRdMatch,
  StrategyRdOffsetKind,
  StrategyRdResolveResponse,
  StrategyRdRuleTemplate,
  StrategyRdScanResponse,
  StrategyRdSizing,
} from "../types/strategyRd";
import type { ExecutionTradeOverlay } from "../types/trade";
import { isApiRequestError } from "./apiRequestError";
import { toApiExecutionCandleType } from "./executionCandleTimeframe";
import {
  findQuoteVariantCandidate,
  normalizeQuoteVariantCandidates,
} from "./instrumentSetupSave";
import { MarketDataUnresolvedError } from "./marketDataResolution";
import { humanizeMarketDataBrokerSlug } from "./marketDataSourceChoice";

/** Longest window per timeframe — mirrors BE settings.LOOKBACK_DAYS_BY_TF. */
export const STRATEGY_RD_LOOKBACK_FINE_MAX_DAYS = 180;
export const STRATEGY_RD_LOOKBACK_COARSE_MAX_DAYS = 720;
/** [max timeframe minutes, max days], first match wins: ≤15m 180d, 30m ~2y, 1h 5y, 2h–4h 10y, 8h+ 20y. */
export const STRATEGY_RD_LOOKBACK_BY_TF: ReadonlyArray<readonly [number, number]> = [
  [15, STRATEGY_RD_LOOKBACK_FINE_MAX_DAYS],
  [30, STRATEGY_RD_LOOKBACK_COARSE_MAX_DAYS],
  [60, 1826],
  [240, 3652],
  [Number.MAX_SAFE_INTEGER, 7305],
];
export const STRATEGY_RD_DEFAULT_LOOKBACK_DAYS = 30;
export const STRATEGY_RD_DEFAULT_CANDLE_TYPE: StrategyRdCandleType = "15m";
export const STRATEGY_RD_QUOTE_VARIANT_SLUG = "market_data_quote_variant_required";

const CANDLE_TYPE_MINUTES: Record<string, number> = {
  "1m": 1,
  m: 1,
  "5m": 5,
  "15m": 15,
  "30m": 30,
  "1h": 60,
  h: 60,
  "4h": 240,
  "1d": 1440,
};

/**
 * Most bars the page holds for the chart: the backtest's whole window (the largest Lab window,
 * 5m crypto × 180 days, is ~52k bars), so every setup and trade in the list sits on loaded
 * candles. The scan stays server-side too (`scan_job_id`).
 */
export const STRATEGY_RD_CHART_BARS = 60_000;

/** Chart load start: the window's start, or `STRATEGY_RD_CHART_BARS` bars back if later. */
export function strategyRdChartStartMs(endMs: number, lookbackDays: number, candleType: string): number {
  const api = toApiExecutionCandleType(candleType) ?? candleType.trim().toLowerCase();
  const minutes = CANDLE_TYPE_MINUTES[api] ?? 15;
  const windowStart = endMs - clampStrategyRdLookbackDays(lookbackDays, candleType) * 86_400_000;
  return Math.max(windowStart, endMs - STRATEGY_RD_CHART_BARS * minutes * 60_000);
}

export function strategyRdLookbackCapDays(candleType: string): number {
  const api = toApiExecutionCandleType(candleType) ?? candleType.trim().toLowerCase();
  const minutes = CANDLE_TYPE_MINUTES[api] ?? 15;
  for (const [maxMinutes, days] of STRATEGY_RD_LOOKBACK_BY_TF) {
    if (minutes <= maxMinutes) return days;
  }
  return STRATEGY_RD_LOOKBACK_BY_TF[STRATEGY_RD_LOOKBACK_BY_TF.length - 1][1];
}

export function clampStrategyRdLookbackDays(
  lookbackDays: number,
  candleType: string
): number {
  const cap = strategyRdLookbackCapDays(candleType);
  if (!Number.isFinite(lookbackDays)) return Math.min(STRATEGY_RD_DEFAULT_LOOKBACK_DAYS, cap);
  return Math.min(cap, Math.max(1, Math.floor(lookbackDays)));
}

export function strategyRdMatchKey(match: Pick<StrategyRdMatch, "pattern_id" | "bar_time" | "symbol">): string {
  return `${match.pattern_id}:${match.symbol}:${match.bar_time}`;
}

const STRATEGY_RD_PATTERN_LABELS: Record<string, string> = {
  bull_engulf: "Bullish engulfing",
  bear_engulf: "Bearish engulfing",
  inside_bar: "Inside bar",
};

export function strategyRdPatternLabel(patternId: string | null | undefined): string {
  const id = patternId?.trim() || "";
  if (!id) return "Pattern";
  return STRATEGY_RD_PATTERN_LABELS[id] ?? id.replace(/_/g, " ");
}

function firstListingToken(
  ...values: Array<string | null | undefined>
): string | undefined {
  for (const value of values) {
    const token = value?.trim();
    if (token) return token;
  }
  return undefined;
}

/**
 * Pass match.chart through to execution-candles as-is, including the scan lane
 * pin. Do not drop market_data_broker / md_source / md_listing_key — those
 * override preference / executing-broker heuristics on the chart request.
 */
export function strategyRdChartToFetchParams(
  chart: StrategyRdChartParams | null | undefined,
  listing?: Pick<StrategyRdMatch, "market_data_broker" | "md_source" | "md_listing_key"> | null
): ExecutionCandlesFetchParams | null {
  if (!chart) return null;
  const symbol = chart.symbol?.trim();
  const brokerAccountCode = chart.broker_account_code?.trim();
  const startDate = chart.start_date?.trim();
  const endDate = chart.end_date?.trim();
  if (!symbol || !brokerAccountCode || !startDate || !endDate) return null;

  const candleType = toApiExecutionCandleType(chart.candle_type) ?? chart.candle_type?.trim();
  const params: ExecutionCandlesFetchParams = {
    symbol,
    broker_account_code: brokerAccountCode,
    start_date: startDate,
    end_date: endDate,
    include_trades: chart.include_trades === true,
    fill_missing: chart.fill_missing !== false,
  };
  if (candleType) params.candle_type = candleType;
  if (chart.asset_class?.trim()) params.asset_class = chart.asset_class.trim();
  const marketDataBroker = firstListingToken(
    chart.market_data_broker,
    listing?.market_data_broker,
    chart.md_source,
    listing?.md_source
  );
  const mdSource = firstListingToken(chart.md_source, listing?.md_source);
  const mdListingKey = firstListingToken(chart.md_listing_key, listing?.md_listing_key);
  if (marketDataBroker) params.market_data_broker = marketDataBroker;
  if (mdSource) params.md_source = mdSource;
  if (mdListingKey) params.md_listing_key = mdListingKey;
  return params;
}

export function isStrategyRdBackfilling(
  response:
    | Pick<StrategyRdScanResponse, "status" | "poll_url" | "job_poll_url">
    | null
    | undefined
): boolean {
  if (!response) return false;
  if (response.status !== "backfilling") return false;
  // job_poll_url is canonical; strip poll_url is optional progress only.
  return Boolean(response.job_poll_url?.trim() || response.poll_url?.trim());
}

export function isStrategyRdQuoteVariantRequired(err: unknown): boolean {
  if (err instanceof MarketDataUnresolvedError) {
    return err.body.error === STRATEGY_RD_QUOTE_VARIANT_SLUG;
  }
  return isApiRequestError(err) && err.slug === STRATEGY_RD_QUOTE_VARIANT_SLUG;
}

function quoteVariantsFromRecord(value: unknown): QuoteVariantCandidate[] {
  if (!value || typeof value !== "object") return [];
  const rec = value as Record<string, unknown>;
  const nested =
    rec.details && typeof rec.details === "object" && !Array.isArray(rec.details)
      ? (rec.details as Record<string, unknown>).quote_variant_candidates
      : undefined;
  return normalizeQuoteVariantCandidates(
    (rec.quote_variant_candidates ?? nested) as
      | QuoteVariantCandidate[]
      | string[]
      | undefined
  );
}

export function strategyRdQuoteVariantsFromError(err: unknown): QuoteVariantCandidate[] {
  if (!isStrategyRdQuoteVariantRequired(err)) return [];
  if (err instanceof MarketDataUnresolvedError) {
    return quoteVariantsFromRecord(err.body);
  }
  if (isApiRequestError(err)) {
    return quoteVariantsFromRecord({
      quote_variant_candidates: err.details?.quote_variant_candidates,
      details: err.details,
    });
  }
  return [];
}

/** Picker `symbol` (e.g. BTCUSDT), never a display label. */
export function strategyRdResolvedSymbolForRequest(
  candidates: QuoteVariantCandidate[],
  selected: string | null | undefined
): string | undefined {
  const match = findQuoteVariantCandidate(candidates, selected);
  const token = (match?.symbol || selected || "").trim();
  return token || undefined;
}

export function strategyRdIdentitySourceLabel(
  listing: Pick<
    StrategyRdResolveResponse,
    "market_data_broker" | "market_data_broker_display"
  > | null
): string | null {
  if (!listing) return null;
  const display = listing.market_data_broker_display?.trim();
  if (display) return display;
  return humanizeMarketDataBrokerSlug(listing.market_data_broker ?? undefined) ?? null;
}

export function defaultStrategyRdRuleTemplate(): StrategyRdRuleTemplate {
  return {
    entry: { mode: "signal_close" },
    stop: {
      anchor: "signal_extreme",
      offset: { kind: "atr_mult", value: 0.25 },
    },
    target: { mode: "r_multiple", value: 2 },
    atr_length: 14,
    pip_size: null,
    tick_size: null,
  };
}

export function strategyRdStopOffsetNeedsValue(kind: StrategyRdOffsetKind): boolean {
  return kind === "pct_price" || kind === "atr_mult" || kind === "pips" || kind === "ticks" || kind === "price";
}

/** beyond_bar / beyond_level: optional ATR buffer — show value field when kind is those. */
export function strategyRdStopOffsetShowsValueField(kind: StrategyRdOffsetKind): boolean {
  return (
    strategyRdStopOffsetNeedsValue(kind) ||
    kind === "beyond_bar" ||
    kind === "beyond_level"
  );
}

/** Side-aware structural anchors — BE picks high vs low from Trade direction. */
const SIDE_AWARE_STRUCTURAL = new Set(["prior_day", "prior_week", "prior_month", "swing"]);
const LEGACY_STRUCTURAL_STOP = new Set([
  "prior_day_low",
  "prior_day_high",
  "prior_week_low",
  "prior_week_high",
  "prior_month_low",
  "prior_month_high",
  "swing_low",
  "swing_high",
]);

export function strategyRdIsSideAwareStructuralAnchor(value: string | null | undefined): boolean {
  return SIDE_AWARE_STRUCTURAL.has((value || "").trim().toLowerCase());
}

/** Map explicit *_low/*_high onto the side-aware picker value. */
export function strategyRdSideAwarePickerValue(raw: string | null | undefined): string {
  const key = (raw || "").trim().toLowerCase();
  if (key === "prior_day_low" || key === "prior_day_high") return "prior_day";
  if (key === "prior_week_low" || key === "prior_week_high") return "prior_week";
  if (key === "prior_month_low" || key === "prior_month_high") return "prior_month";
  if (key === "swing_low" || key === "swing_high") return "swing";
  return (raw || "").trim() || key;
}

export function strategyRdIsStructuralStopAnchor(anchor: string | null | undefined): boolean {
  const key = (anchor || "").trim().toLowerCase();
  return SIDE_AWARE_STRUCTURAL.has(key) || LEGACY_STRUCTURAL_STOP.has(key);
}

export function strategyRdIsStructuralTargetMode(mode: string | null | undefined): boolean {
  const key = (mode || "").trim().toLowerCase();
  return (
    SIDE_AWARE_STRUCTURAL.has(key) ||
    LEGACY_STRUCTURAL_STOP.has(key) ||
    key === "level_extreme"
  );
}

export function strategyRdUsesPipOrTick(
  template: Pick<StrategyRdRuleTemplate, "stop" | "target">
): boolean {
  const stopKind = template.stop.offset.kind;
  const targetMode = template.target.mode;
  return (
    stopKind === "pips" ||
    stopKind === "ticks" ||
    targetMode === "pips" ||
    targetMode === "ticks"
  );
}

export function strategyRdHypothesisName(
  patternId: string,
  template: StrategyRdRuleTemplate
): string {
  const pattern = strategyRdPatternLabel(patternId);
  if (template.target.mode === "r_multiple" && Number.isFinite(template.target.value)) {
    const r = template.target.value;
    const label = Number.isInteger(r) ? `${r}` : String(r);
    return `${pattern} ${label}R`;
  }
  return pattern;
}

export function formatStrategyRdPrice(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "N/A";
  const abs = Math.abs(value);
  if (abs >= 100) return value.toFixed(2);
  if (abs >= 1) return value.toFixed(4);
  return value.toFixed(5);
}

export function strategyRdOutcomeLabel(result: StrategyRdForwardResult | string | null | undefined): string {
  if (result === "target") return "Target";
  if (result === "stop") return "Stop";
  if (result === "open" || result === "neither") return "Open";
  if (result === "skipped") return "Skipped";
  if (result === "liquidated") return "Liquidated";
  if (result === "exit") return "Time stop";
  return "N/A";
}

/** Normalize BE / legacy apply outcomes (`neither` → `open`). */
export function normalizeStrategyRdForwardResult(
  raw: string | null | undefined
): StrategyRdForwardResult {
  const result = (raw || "").trim().toLowerCase();
  if (
    result === "target" ||
    result === "stop" ||
    result === "skipped" ||
    result === "liquidated" ||
    result === "exit"
  ) {
    return result;
  }
  if (result === "open" || result === "neither") return "open";
  return "skipped";
}

export function strategyRdExcursionUnitLabel(
  unit: StrategyRdExcursionUnit | string | null | undefined
): string {
  switch ((unit || "").trim().toLowerCase()) {
    case "atr":
      return "ATR";
    case "r":
      return "R";
    case "pct_price":
      return "%";
    case "pips":
      return "pips";
    case "ticks":
      return "ticks";
    case "price":
      return "price";
    default:
      return "";
  }
}

function asFiniteOrNull(value: unknown): number | null {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

export function formatStrategyRdWinRate(rate: number | null | undefined): string | null {
  const n = asFiniteOrNull(rate);
  if (n == null) return null;
  const pct = n * 100;
  const rounded = Math.abs(pct - Math.round(pct)) < 0.05 ? Math.round(pct) : Math.round(pct * 10) / 10;
  return `${rounded}%`;
}

export function formatStrategyRdRMultiple(value: number | null | undefined): string | null {
  const n = asFiniteOrNull(value);
  if (n == null) return null;
  const sign = n < 0 ? "−" : "";
  const mag = Math.abs(n);
  const text = mag >= 10 ? mag.toFixed(1) : mag >= 1 ? mag.toFixed(1) : mag.toFixed(2);
  return `${sign}${text}R`;
}

/** Realized reward:risk vs a full −1R stop (e.g. `4.7 : 1`). */
export function formatStrategyRdRewardRisk(avgWinR: number | null | undefined): string | null {
  const n = asFiniteOrNull(avgWinR);
  if (n == null || n <= 0) return null;
  const mag = Math.abs(n);
  const text = mag >= 10 ? mag.toFixed(1) : mag >= 1 ? mag.toFixed(1) : mag.toFixed(2);
  return `${text} : 1`;
}

export function formatStrategyRdBarsToEvent(opts: {
  /** Primary center value (prefer median when available). */
  value?: number | null;
  /** @deprecated use `value` — kept for older call sites. */
  avg?: number | null;
  min?: number | null;
  max?: number | null;
}): string | null {
  const center = asFiniteOrNull(opts.value ?? opts.avg);
  if (center == null) return null;
  const avgText = Number.isInteger(center) ? String(center) : center.toFixed(center < 10 ? 1 : 0);
  const min = asFiniteOrNull(opts.min);
  const max = asFiniteOrNull(opts.max);
  if (min != null && max != null) {
    return `${avgText} (${Math.round(min)}–${Math.round(max)})`;
  }
  return avgText;
}

/** "45m", "6h", "1.5d" — a holding time for people (bars are abstract). */
export function formatStrategyRdDurationMs(ms: number): string {
  const minutes = ms / 60_000;
  if (minutes < 60) return `${Math.max(1, Math.round(minutes))}m`;
  const hours = minutes / 60;
  if (hours < 48) return `${hours < 10 ? Number(hours.toFixed(1)) : Math.round(hours)}h`;
  const days = hours / 24;
  return `${days < 10 ? Number(days.toFixed(1)) : Math.round(days)}d`;
}

/** Entry → exit durations (ms) of closed trades with both times. */
export function strategyRdHoldDurationsMs(
  rows: ReadonlyArray<{ entry_time?: string | null; exit_time?: string | null } | null | undefined>
): number[] {
  const out: number[] = [];
  for (const row of rows) {
    const a = Date.parse((row?.entry_time || "").trim());
    const b = Date.parse((row?.exit_time || "").trim());
    if (Number.isFinite(a) && Number.isFinite(b) && b >= a) out.push(b - a);
  }
  return out;
}

/** "6h (45m–3d)": median (min–max) time from entry to take profit or stop. */
export function formatStrategyRdTimeToExit(
  rows: ReadonlyArray<{ entry_time?: string | null; exit_time?: string | null } | null | undefined>
): string | null {
  const durations = strategyRdHoldDurationsMs(rows).sort((x, y) => x - y);
  if (durations.length === 0) return null;
  const mid = Math.floor(durations.length / 2);
  const median =
    durations.length % 2 === 1 ? durations[mid]! : (durations[mid - 1]! + durations[mid]!) / 2;
  const text = formatStrategyRdDurationMs(median);
  if (durations.length === 1) return text;
  return `${text} (${formatStrategyRdDurationMs(durations[0]!)}\u2013${formatStrategyRdDurationMs(durations[durations.length - 1]!)})`;
}

export function formatStrategyRdExcursionValue(
  value: number | null | undefined,
  unit?: StrategyRdExcursionUnit | string | null
): string | null {
  const n = asFiniteOrNull(value);
  if (n == null) return null;
  const abs = Math.abs(n);
  let body: string;
  if (abs >= 100) body = n.toFixed(1);
  else if (abs >= 10) body = n.toFixed(1);
  else {
    body = n.toFixed(2).replace(/\.?0+$/, "");
    if (!body.includes(".")) body = n.toFixed(1).replace(/\.0$/, "");
  }
  const label = strategyRdExcursionUnitLabel(unit);
  return label ? `${body} ${label}` : body;
}

/** Median headline with min–max range; avg reserved for tooltips. */
export function formatStrategyRdExcursionSummary(opts: {
  median?: number | null;
  avg?: number | null;
  min?: number | null;
  max?: number | null;
  unit?: StrategyRdExcursionUnit | string | null;
}): { amount: string; tooltip: string } | null {
  const median = asFiniteOrNull(opts.median) ?? asFiniteOrNull(opts.avg);
  if (median == null) return null;
  const unitLabel = strategyRdExcursionUnitLabel(opts.unit);
  const medianBody = formatStrategyRdExcursionValue(median, null);
  if (!medianBody) return null;

  const min = asFiniteOrNull(opts.min);
  const max = asFiniteOrNull(opts.max);
  const minBody = min != null ? formatStrategyRdExcursionValue(min, null) : null;
  const maxBody = max != null ? formatStrategyRdExcursionValue(max, null) : null;

  let amount =
    unitLabel != null && unitLabel !== ""
      ? `${medianBody} ${unitLabel}`
      : medianBody;
  if (minBody && maxBody) {
    amount =
      unitLabel != null && unitLabel !== ""
        ? `${medianBody} ${unitLabel} (${minBody}–${maxBody})`
        : `${medianBody} (${minBody}–${maxBody})`;
  }

  const avg = asFiniteOrNull(opts.avg);
  const avgText = avg != null ? formatStrategyRdExcursionValue(avg, opts.unit) : null;
  const tooltipParts = [
    "Median (min–max); includes open paths",
    avgText ? `Avg ${avgText}` : null,
  ].filter(Boolean);
  return { amount, tooltip: tooltipParts.join(" · ") };
}

export function formatStrategyRdHitRate(opts: {
  targetCount?: number | null;
  stopCount?: number | null;
}): string | null {
  const t = asFiniteOrNull(opts.targetCount);
  const s = asFiniteOrNull(opts.stopCount);
  const bits = [
    t != null ? `${Math.round(t)} TP` : null,
    s != null ? `${Math.round(s)} SL` : null,
  ].filter(Boolean);
  return bits.length ? bits.join(" / ") : null;
}

/** Null-safe overview strip segments — omit missing fields. */
export function strategyRdOverviewStripParts(
  overview: StrategyRdApplyOverview | null | undefined
): string[] {
  if (!overview) return [];
  const parts: string[] = [];
  const matches = asFiniteOrNull(overview.match_count);
  if (matches != null) {
    parts.push(`${Math.round(matches)} match${matches === 1 ? "" : "es"}`);
  }

  const hit = formatStrategyRdHitRate({
    targetCount: overview.target_count,
    stopCount: overview.stop_count,
  });
  if (hit) parts.push(hit);

  const open = asFiniteOrNull(overview.open_count);
  if (open != null) parts.push(`${Math.round(open)} Open`);

  const win = formatStrategyRdWinRate(overview.win_rate);
  if (win) parts.push(`Win ${win}`);

  const exp = formatStrategyRdRMultiple(overview.expectancy_r ?? overview.avg_r);
  if (exp) parts.push(`Exp ${exp}`);

  const sumR = formatStrategyRdRMultiple(overview.sum_r);
  if (sumR) parts.push(`Sum ${sumR}`);

  const pf = asFiniteOrNull(overview.profit_factor_r);
  if (pf != null) {
    parts.push(`PF ${pf >= 10 ? pf.toFixed(1) : pf.toFixed(2)}`);
  }

  const bars = formatStrategyRdBarsToEvent({
    value: overview.median_bars_to_event ?? overview.avg_bars_to_event,
    min: overview.min_bars_to_event,
    max: overview.max_bars_to_event,
  });
  if (bars) parts.push(`Bars ${bars}`);

  const risk = asFiniteOrNull(overview.median_risk) ?? asFiniteOrNull(overview.avg_risk);
  if (risk != null) {
    parts.push(`Risk ${formatStrategyRdPrice(risk)}`);
  }

  const unit = overview.excursion_unit;
  const mfe = formatStrategyRdExcursionSummary({
    median: overview.median_mfe,
    avg: overview.avg_mfe,
    min: overview.min_mfe,
    max: overview.max_mfe,
    unit,
  });
  if (mfe) parts.push(`MFE ${mfe.amount}`);
  const mae = formatStrategyRdExcursionSummary({
    median: overview.median_mae,
    avg: overview.avg_mae,
    min: overview.min_mae,
    max: overview.max_mae,
    unit,
  });
  if (mae) parts.push(`MAE ${mae.amount}`);

  const rr = formatStrategyRdRewardRisk(overview.avg_win_r);
  if (rr) parts.push(`R:R ${rr}`);

  return parts;
}

export function formatStrategyRdOverviewStrip(
  overview: StrategyRdApplyOverview | null | undefined
): string | null {
  const parts = strategyRdOverviewStripParts(overview);
  return parts.length ? parts.join(" · ") : null;
}

export function formatStrategyRdOutcomeCell(opts: {
  result?: StrategyRdForwardResult | string | null;
  barsToEvent?: number | null;
}): string {
  const label = strategyRdOutcomeLabel(opts.result);
  const bars = asFiniteOrNull(opts.barsToEvent);
  const closed =
    opts.result === "target" ||
    opts.result === "stop" ||
    opts.result === "neither" ||
    opts.result === "liquidated" ||
    opts.result === "exit";
  if (closed && bars != null) {
    return `${label} · ${Math.round(bars)}b`;
  }
  return label;
}

export function formatStrategyRdRowExcursion(
  excursion: StrategyRdExcursion | null | undefined
): string | null {
  if (!excursion) return null;
  const mfe = formatStrategyRdExcursionValue(excursion.mfe, excursion.unit);
  const mae = formatStrategyRdExcursionValue(excursion.mae, excursion.unit);
  if (!mfe && !mae) return null;
  if (mfe && mae) return `MFE ${mfe} · MAE ${mae}`;
  if (mfe) return `MFE ${mfe}`;
  return `MAE ${mae}`;
}

/** Format Apply / Research sim money values (plain $ — not display-currency). */
/**
 * Money in the account currency. USD (the default) prints `$`; an unconverted
 * quote currency (Apply `currency.converted: false`, e.g. JPY) prints its code.
 */
export function formatStrategyRdMoney(
  value: number | null | undefined,
  currency?: string | null
): string | null {
  const n = asFiniteOrNull(value);
  if (n == null) return null;
  const sign = n < 0 ? "−" : "";
  const mag = Math.abs(n);
  const text =
    mag >= 1000
      ? mag.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })
      : mag.toFixed(2);
  const code = strategyRdMoneyCurrency(currency);
  return code === "USD" ? `${sign}$${text}` : `${sign}${text} ${code}`;
}

/** Account currency code for money columns: USD unless the BE says otherwise. */
export function strategyRdMoneyCurrency(currency?: string | null): string {
  const code = (currency || "").trim().toUpperCase();
  return code || "USD";
}

/** Money column suffix, e.g. "PnL $" or "PnL JPY". */
export function strategyRdMoneyColumnLabel(label: string, currency?: string | null): string {
  const code = strategyRdMoneyCurrency(currency);
  return `${label} ${code === "USD" ? "$" : code}`;
}

/**
 * Asset-class default leverage (BE `default_leverage`) — placeholder only; the
 * BE resolves the real value and Apply echoes it.
 */
export function strategyRdDefaultLeverage(opts: {
  assetClass?: string | null;
  symbol?: string | null;
}): number {
  const ac = (opts.assetClass || "").trim().toLowerCase();
  if (ac === "equity" || ac === "stock" || ac === "etf") return 1;
  if (ac === "fx" || ac === "forex") return 30;
  if (ac === "metal" || ac === "index" || ac === "commodity" || ac === "energy") return 20;
  if (ac === "crypto") return 2;
  const sym = (opts.symbol || "").trim().toUpperCase().replace(/[^A-Z]/g, "");
  if (/^(XAU|XAG|XPT|XPD)/.test(sym)) return 20;
  if (/(USDT|USDC)$/.test(sym) || /^(BTC|ETH)/.test(sym)) return 2;
  if (/^[A-Z]{6}$/.test(sym)) return 30;
  return 1;
}

export function formatStrategyRdLeverage(value: number | null | undefined): string | null {
  const n = asFiniteOrNull(value);
  if (n == null) return null;
  return `${Number.isInteger(n) ? n : n.toFixed(2).replace(/\.?0+$/, "")}×`;
}

const STRATEGY_RD_SKIPPED_REASON_LABELS: Record<string, string> = {
  insufficient_margin: "Not enough free margin",
  opposite_position_open: "Opposite position open",
  position_open: "A trade was already open",
  daily_trade_limit: "Daily trade limit reached",
  target_below_min_r: "Target under the minimum reward",
  daily_loss_limit: "Daily loss limit reached",
};

const STRATEGY_RD_OUTCOME_NOTE_LABELS: Record<string, { label: string; tip: string }> = {
  break_even: { label: "Break-even", tip: "The stop had moved to break-even (Manage the trade)." },
  time_stop: { label: "Time stop", tip: "Closed by the time stop after N bars." },
  partial: { label: "Partials", tip: "Closed in parts. Expand the row for each piece." },
  time_exit: { label: "Clock exit", tip: "Closed at the clock time set in Manage the trade." },
  oscillator_exit: { label: "StochRSI exit", tip: "Closed on the opposite Stochastic RSI cross." },
  ma_close_exit: { label: "MA close exit", tip: "Closed when a candle closed through the moving average." },
  gap_open: { label: "Gap", tip: "Gapped past the level. Filled at the bar's open, as on TradingView." },
  same_bar_both: {
    label: "Same bar",
    tip: "Stop and target were both inside one candle; the bar path decided which came first.",
  },
};

/** `forward_outcome.note` chips (`break_even|partial`, …) — unknown notes are ignored. */
export function strategyRdOutcomeNoteChips(
  note: string | null | undefined
): Array<{ key: string; label: string; tip: string }> {
  return (note || "")
    .split("|")
    .map((k) => k.trim())
    .filter((k, i, all) => k && all.indexOf(k) === i && STRATEGY_RD_OUTCOME_NOTE_LABELS[k])
    .map((key) => ({ key, ...STRATEGY_RD_OUTCOME_NOTE_LABELS[key] }));
}

/** Why a fill was skipped (`fill.skipped_reason`). */
export function strategyRdSkippedReasonLabel(reason: string | null | undefined): string | null {
  const key = (reason || "").trim();
  if (!key) return null;
  return STRATEGY_RD_SKIPPED_REASON_LABELS[key] ?? key.replace(/_/g, " ");
}

export function formatStrategyRdPct(value: number | null | undefined): string | null {
  const n = asFiniteOrNull(value);
  if (n == null) return null;
  const sign = n < 0 ? "−" : "";
  const mag = Math.abs(n);
  const text = mag >= 10 ? mag.toFixed(1) : mag.toFixed(2);
  return `${sign}${text}%`;
}

export function formatStrategyRdQty(value: number | null | undefined): string {
  const n = asFiniteOrNull(value);
  if (n == null) return "N/A";
  if (Number.isInteger(n)) return String(n);
  const abs = Math.abs(n);
  if (abs >= 1) return n.toFixed(4).replace(/\.?0+$/, "");
  return n.toFixed(6).replace(/\.?0+$/, "");
}

export function isValidStrategyRdSizing(
  startingEquity: number | null | undefined,
  sizing: StrategyRdSizing | null | undefined
): boolean {
  if (startingEquity == null || !Number.isFinite(startingEquity) || startingEquity <= 0) {
    return false;
  }
  if (!sizing) return false;
  if (sizing.mode !== "risk_pct" && sizing.mode !== "fixed_qty") {
    return false;
  }
  return Number.isFinite(sizing.value) && sizing.value > 0;
}

/**
 * Apply body sizing: account / execution fields only when set, so the BE picks
 * its defaults (asset leverage, `shared`, no costs).
 */
export function strategyRdSizingForWire(sizing: StrategyRdSizing): StrategyRdSizing {
  const out: StrategyRdSizing = { mode: sizing.mode, value: sizing.value };
  if (sizing.leverage != null && sizing.leverage > 0) out.leverage = sizing.leverage;
  if (sizing.position_mode) out.position_mode = sizing.position_mode;
  if (sizing.commission_pct != null && sizing.commission_pct > 0) {
    out.commission_pct = sizing.commission_pct;
  }
  if (sizing.slippage_ticks != null && sizing.slippage_ticks > 0) {
    out.slippage_ticks = Math.floor(sizing.slippage_ticks);
  }
  if (sizing.spread != null && sizing.spread > 0) out.spread = sizing.spread;
  if (sizing.spread_pct != null && sizing.spread_pct > 0) out.spread_pct = sizing.spread_pct;
  if (sizing.swap_long_pct != null && sizing.swap_long_pct !== 0) out.swap_long_pct = sizing.swap_long_pct;
  if (sizing.swap_short_pct != null && sizing.swap_short_pct !== 0) out.swap_short_pct = sizing.swap_short_pct;
  const trades = sizing.limits?.max_trades_per_day;
  const losses = sizing.limits?.max_losses_per_day;
  if ((trades != null && trades >= 1) || (losses != null && losses >= 1)) {
    out.limits = {
      ...(trades != null && trades >= 1 ? { max_trades_per_day: Math.floor(trades) } : {}),
      ...(losses != null && losses >= 1 ? { max_losses_per_day: Math.floor(losses) } : {}),
    };
  }
  return out;
}

/** Confirm gate: sized Apply on last_apply or overview. */
export function strategyRdHasSizedApply(opts: {
  startingEquity?: number | null;
  sizing?: StrategyRdSizing | null;
  overview?: StrategyRdApplyOverview | null;
  lastApply?: StrategyRdLastApply | null;
}): boolean {
  const equity =
    opts.startingEquity ??
    opts.lastApply?.starting_equity ??
    opts.overview?.starting_equity ??
    opts.lastApply?.overview?.starting_equity ??
    null;
  const sizing =
    opts.sizing ??
    opts.lastApply?.sizing ??
    opts.overview?.sizing ??
    opts.lastApply?.overview?.sizing ??
    null;
  return isValidStrategyRdSizing(equity, sizing);
}

/** Default Confirm name: `{pattern} · {symbol} · {candle_type}`. */
export function strategyRdConfirmDefaultName(opts: {
  patternId?: string | null;
  symbol?: string | null;
  candleType?: string | null;
}): string {
  const pattern = strategyRdPatternLabel(opts.patternId);
  const symbol = (opts.symbol || "").trim() || "N/A";
  const tf = (opts.candleType || "").trim() || "N/A";
  return `${pattern} · ${symbol} · ${tf}`;
}

export function strategyRdDefaultOffsetValue(kind: StrategyRdOffsetKind): number {
  if (kind === "atr_mult") return 0.25;
  if (kind === "pct_price") return 0.5;
  if (kind === "pips" || kind === "ticks") return 10;
  if (kind === "price") return 1;
  return 0;
}

/**
 * Unit size for pips/ticks when the user has not set one.
 * FX default 0.0001 is invisible on XAU (~4400) — metals/equity need ~0.01.
 */
export function defaultStrategyRdUnitSize(opts?: {
  symbol?: string | null;
  assetClass?: string | null;
}): number {
  const symbol = (opts?.symbol || "").trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
  const asset = (opts?.assetClass || "").trim().toLowerCase();
  if (
    asset === "metal" ||
    asset === "metals" ||
    symbol.startsWith("XAU") ||
    symbol.startsWith("XAG") ||
    symbol.startsWith("XPT") ||
    symbol.startsWith("XPD")
  ) {
    return 0.01;
  }
  if (asset === "equity" || asset === "index" || asset === "indices") return 0.01;
  if (asset === "crypto") return 0.01;
  if (symbol.endsWith("JPY") || symbol.includes("JPY")) return 0.01;
  return 0.0001;
}

export function resolveStrategyRdPipSize(
  template: Pick<StrategyRdRuleTemplate, "pip_size">,
  opts?: { symbol?: string | null; assetClass?: string | null }
): number {
  const set = template.pip_size;
  if (typeof set === "number" && Number.isFinite(set) && set > 0) return set;
  return defaultStrategyRdUnitSize(opts);
}

export function resolveStrategyRdTickSize(
  template: Pick<StrategyRdRuleTemplate, "pip_size" | "tick_size">,
  opts?: { symbol?: string | null; assetClass?: string | null }
): number {
  const set = template.tick_size;
  if (typeof set === "number" && Number.isFinite(set) && set > 0) return set;
  return resolveStrategyRdPipSize(template, opts);
}

/** Fill null pip/tick with symbol-aware defaults before Apply / BE. */
export function withResolvedStrategyRdUnitSizes(
  template: StrategyRdRuleTemplate,
  opts?: { symbol?: string | null; assetClass?: string | null }
): StrategyRdRuleTemplate {
  if (!strategyRdUsesPipOrTick(template)) return template;
  const pip = resolveStrategyRdPipSize(template, opts);
  const tick = resolveStrategyRdTickSize(template, opts);
  if (template.pip_size === pip && template.tick_size === tick) return template;
  return {
    ...template,
    pip_size: template.pip_size != null && template.pip_size > 0 ? template.pip_size : pip,
    tick_size: template.tick_size != null && template.tick_size > 0 ? template.tick_size : tick,
  };
}

/** Synthetic selected trade so Vela draws the Trade Review position tool. */
export const STRATEGY_RD_LEVELS_TRADE_ID = "strategy-rd-levels";
export const STRATEGY_RD_ZONE_TRADE_ID = "strategy-rd-meta-zone";

export function strategyRdLevelsToTradeOverlay(opts: {
  match: StrategyRdMatch;
  levels: StrategyRdAppliedLevels;
  /** Overrides chart.end_date — use forward-bars end for live preview. */
  exitTime?: string | null;
}): ExecutionTradeOverlay | null {
  const { match, levels } = opts;
  if (!levels.valid) return null;
  if (
    !Number.isFinite(levels.entry) ||
    !Number.isFinite(levels.stop) ||
    !Number.isFinite(levels.target)
  ) {
    return null;
  }
  const direction = levels.direction === "short" ? "short" : "long";
  const rr =
    levels.risk > 0 ? Math.abs(levels.target - levels.entry) / levels.risk : null;
  const exitTime = opts.exitTime?.trim() || match.chart?.end_date?.trim() || null;
  return {
    trade_id: STRATEGY_RD_LEVELS_TRADE_ID,
    symbol: match.symbol,
    direction,
    entry_time: match.bar_time,
    exit_time: exitTime,
    entry_price: levels.entry,
    exit_price: null,
    sl_price: levels.stop,
    tp_price: levels.target,
    pips_to_tp: null,
    pips_to_sl: null,
    rr,
    is_win: false,
    is_loss: false,
    net_pnl: null,
  };
}

/** Draw Concepts meta zone as a position band (low→high) when Apply levels are absent. */
export function strategyRdMetaZoneToTradeOverlay(opts: {
  match: StrategyRdMatch;
  high: number;
  low: number;
}): ExecutionTradeOverlay | null {
  const { match, high, low } = opts;
  if (!Number.isFinite(high) || !Number.isFinite(low)) return null;
  const top = Math.max(high, low);
  const bottom = Math.min(high, low);
  const mid = (top + bottom) / 2;
  const direction = match.direction === "short" ? "short" : "long";
  const exitTime = match.chart?.end_date?.trim() || null;
  return {
    trade_id: STRATEGY_RD_ZONE_TRADE_ID,
    symbol: match.symbol,
    direction,
    entry_time: match.bar_time,
    exit_time: exitTime,
    entry_price: mid,
    exit_price: null,
    sl_price: direction === "long" ? bottom : top,
    tp_price: direction === "long" ? top : bottom,
    pips_to_tp: null,
    pips_to_sl: null,
    rr: top > bottom ? (top - bottom) / Math.max(top - bottom, 1e-9) : null,
    is_win: false,
    is_loss: false,
    net_pnl: null,
  };
}
